import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions, Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { CubeIcon } from '@heroicons/react/24/outline';
import { FunctionComponent, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CertificateStatus } from 'softcat:components/certificate-status.tsx';
import { Spinner } from 'softcat:components/spinner.tsx';
import { useTerritory } from 'softcat:hooks/use-territory.ts';
import { ConfigService } from 'softcat:services/config.ts';
import { WidgetService } from 'softcat:services/widget.ts';
import { SoftcatApiWidget } from 'softcat:types/softcat-api.ts';
import { WidgetUtils } from 'softcat:utils/widget.ts';
import useSWR from 'swr';
import { useDebounceCallback } from 'usehooks-ts';

type CatalogueWidgetModalProperties = {
    handleClose: () => void;
    handleConfirm: (widget: SoftcatApiWidget) => void;
    isVisible: boolean;
};

export const CatalogueWidgetModal: FunctionComponent<CatalogueWidgetModalProperties> = ({ handleClose, handleConfirm, isVisible }) => {
    const intl = useIntl();
    const { activeTerritory } = useTerritory();

    const [query, setQuery] = useState('');
    const { data: platformsData = [] } = useSWR({ key: `${activeTerritory}-ConfigServiceFindAllPlatforms` }, ConfigService.findAllPlatforms);
    const { data, error, isLoading } = useSWR(
        { key: `${activeTerritory}-WidgetServiceFindAll`, limit: query ? 200 : 100, orderBy: query ? 'version:desc' : 'uploadedDate:desc', query },
        WidgetService.findAll,
    );

    useEffect(() => {
        if (!isVisible) {
            setQuery('');
        }
    }, [isVisible]);

    const handleSearch = (widget: SoftcatApiWidget) => {
        if (widget) {
            handleConfirm(widget as SoftcatApiWidget);
        }
    };

    const updateQueryTerm = useDebounceCallback((updatedQueryValue) => {
        setQuery(updatedQueryValue);
    }, 500);

    return (
        <Dialog className="relative z-[60]" data-testid={'catalogue-widget-modal'} onClose={handleClose} open={isVisible}>
            <DialogBackdrop
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
                transition
            />

            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-6 md:p-20">
                    <DialogPanel
                        className="relative transform space-y-2 overflow-hidden rounded-lg bg-white p-2 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-2xl data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                        transition
                    >
                        <Combobox onChange={handleSearch}>
                            <ComboboxInput
                                autoFocus
                                className="w-full rounded-md border-0 bg-gray-100 px-4 py-2.5 text-gray-900 focus:ring-0 sm:text-sm"
                                defaultValue={query}
                                onChange={(event) => {
                                    updateQueryTerm(event.currentTarget.value);
                                }}
                                placeholder={intl.formatMessage({ id: 'CATALOGUES_EDIT_PAGE_FORM_ADD_APP_SEARCH_PLACEHOLDER' })}
                            />

                            {isLoading && (
                                <div className={'flex h-96 items-center justify-center'}>
                                    <Spinner />
                                </div>
                            )}

                            {error && (
                                <div className={'flex h-96 items-center justify-center'}>
                                    <FormattedMessage id={'CATALOGUES_EDIT_PAGE_FORM_ADD_APP_ERROR_LOADING_DATA'} />
                                </div>
                            )}

                            {data && (
                                <div>
                                    {data.length > 0 && (
                                        <ComboboxOptions className="h-96 overflow-y-auto pb-2 text-sm text-gray-800" static>
                                            {data.map((widget) => {
                                                const platformIds = WidgetUtils.matchPlatforms({ platforms: platformsData, widget });
                                                const platformVariants = WidgetUtils.extractPlatformVariants({ widget });

                                                return (
                                                    <ComboboxOption
                                                        className="group cursor-pointer select-none rounded-md px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-indigo-600 hover:text-white"
                                                        key={`${widget.id}-${widget.version}`}
                                                        value={widget}
                                                    >
                                                        <div className={'flex items-center gap-2 leading-6'}>
                                                            <CubeIcon className={'size-5 shrink-0 translate-y-0.5 text-gray-300 group-hover:text-white'} />
                                                            <CertificateStatus certificateExpiry={widget.publisherNotAfter} showText={false} />
                                                            <span>{widget.name}</span>
                                                            <span className="text-xs text-gray-400 group-hover:text-cyan-400">{widget.version}</span>
                                                        </div>
                                                        {(platformVariants || platformIds) && (
                                                            <div className="pl-12 indent-0.5 text-xxs text-stone-400 group-hover:text-white">
                                                                {platformVariants && (
                                                                    <div data-testid={'widget-platform-variants'}>{platformVariants.join(', ')}</div>
                                                                )}
                                                                {platformIds && <div data-testid={'widget-platform-ids'}>{platformIds.join(', ')}</div>}
                                                            </div>
                                                        )}
                                                    </ComboboxOption>
                                                );
                                            })}
                                        </ComboboxOptions>
                                    )}

                                    {query !== '' && data.length === 0 && (
                                        <div className={'flex h-96 flex-col items-center justify-center gap-3'}>
                                            <CubeIcon className={'size-7 text-gray-300'} />
                                            <FormattedMessage id={'CATALOGUES_EDIT_PAGE_FORM_ADD_APP_NO_RESULTS'} />
                                        </div>
                                    )}
                                </div>
                            )}
                        </Combobox>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
};
